import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
  Fatal = 4
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  category: string;
  userId?: string;
  companyNo?: string;
  operatorId?: string;
  operatorName?: string;
  sessionId: string;
  userAgent: string;
  url: string;
  properties?: { [key: string]: any };
  exception?: {
    type: string;
    message: string;
    stackTrace?: string;
  };
}

export interface ClientLogRequest {
  logs: LogEntry[];
}

class LoggingService {
  private sessionId: string;
  private logBuffer: LogEntry[] = [];
  private readonly bufferSize = 10;
  private readonly flushInterval = 30000; // 30 sekunder
  private flushTimer?: NodeJS.Timeout;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.startFlushTimer();
    
    // Send logs når bruker lukker siden
    window.addEventListener('beforeunload', () => {
      this.flush(true); // Sync flush
    });

    // Global error handler
    window.addEventListener('error', (event) => {
      this.error('Global Error Handler', event.error?.message || 'Unknown error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.stack
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled Promise Rejection', event.reason?.message || 'Promise rejected', {
        reason: event.reason
      });
    });
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      if (this.logBuffer.length > 0) {
        this.flush();
      }
    }, this.flushInterval);
  }

  private getUserContext() {
    const userData = AuthService.getUserDataFromToken();
    return userData ? {
      userId: userData.operatorId,
      companyNo: userData.companyNo,
      operatorId: userData.operatorId,
      operatorName: userData.operatorName
    } : {};
  }

  private createLogEntry(
    level: LogLevel,
    category: string,
    message: string,
    properties?: { [key: string]: any },
    exception?: { type: string; message: string; stackTrace?: string }
  ): LogEntry {
    const userContext = this.getUserContext();
    
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      category,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      properties,
      exception,
      ...userContext
    };
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);
    
    // Console fallback for development
    if (process.env.NODE_ENV === 'development') {
      const levelName = LogLevel[entry.level];
      console.log(`[${levelName}] [${entry.category}] ${entry.message}`, entry.properties);
    }
    
    // Auto-flush on buffer full or high severity
    if (this.logBuffer.length >= this.bufferSize || entry.level >= LogLevel.Error) {
      this.flush();
    }
  }

  private async flush(sync: boolean = false): Promise<void> {
    if (this.logBuffer.length === 0) return;

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    const request: ClientLogRequest = {
      logs: logsToSend
    };

    try {
      if (sync) {
        // Synchronous request for beforeunload
        const blob = new Blob([JSON.stringify(request)], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE_URL}/logging/client`, blob);
      } else {
        // Asynchronous request
        await fetch(`${API_BASE_URL}/logging/client`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(AuthService.getToken() ? { 'Authorization': `Bearer ${AuthService.getToken()}` } : {})
          },
          body: JSON.stringify(request)
        });
      }
    } catch (error) {
      // Fallback til console hvis server ikke er tilgjengelig
      console.warn('Failed to send logs to server:', error);
      logsToSend.forEach(log => {
        const levelName = LogLevel[log.level];
        console.log(`[${levelName}] [${log.category}] ${log.message}`, log.properties);
      });
    }
  }

  // Public logging methods
  debug(category: string, message: string, properties?: { [key: string]: any }): void {
    const entry = this.createLogEntry(LogLevel.Debug, category, message, properties);
    this.addToBuffer(entry);
  }

  info(category: string, message: string, properties?: { [key: string]: any }): void {
    const entry = this.createLogEntry(LogLevel.Info, category, message, properties);
    this.addToBuffer(entry);
  }

  warn(category: string, message: string, properties?: { [key: string]: any }): void {
    const entry = this.createLogEntry(LogLevel.Warn, category, message, properties);
    this.addToBuffer(entry);
  }

  error(category: string, message: string, properties?: { [key: string]: any }): void {
    const entry = this.createLogEntry(LogLevel.Error, category, message, properties);
    this.addToBuffer(entry);
  }

  fatal(category: string, message: string, properties?: { [key: string]: any }): void {
    const entry = this.createLogEntry(LogLevel.Fatal, category, message, properties);
    this.addToBuffer(entry);
    this.flush(); // Immediate flush for fatal errors
  }

  // Convenience method for exceptions
  logException(category: string, error: Error, additionalProperties?: { [key: string]: any }): void {
    const properties = {
      ...additionalProperties,
      errorType: error.constructor.name
    };

    const exception = {
      type: error.constructor.name,
      message: error.message,
      stackTrace: error.stack
    };

    const entry = this.createLogEntry(LogLevel.Error, category, error.message, properties, exception);
    this.addToBuffer(entry);
  }

  // API call logging helper
  logApiCall(method: string, url: string, statusCode?: number, duration?: number, error?: Error): void {
    const level = error ? LogLevel.Error : (statusCode && statusCode >= 400 ? LogLevel.Warn : LogLevel.Info);
    const message = error ? `API call failed: ${method} ${url}` : `API call: ${method} ${url}`;
    
    const properties = {
      method,
      url,
      statusCode,
      duration,
      ...(error && { errorMessage: error.message })
    };

    const entry = this.createLogEntry(level, 'API', message, properties, 
      error ? { type: error.constructor.name, message: error.message, stackTrace: error.stack } : undefined);
    
    this.addToBuffer(entry);
  }

  // User action logging
  logUserAction(action: string, properties?: { [key: string]: any }): void {
    this.info('UserAction', action, properties);
  }

  // Performance logging
  logPerformance(operation: string, duration: number, properties?: { [key: string]: any }): void {
    const level = duration > 5000 ? LogLevel.Warn : LogLevel.Info; // Warn if > 5 seconds
    this.addToBuffer(this.createLogEntry(level, 'Performance', `${operation} took ${duration}ms`, { duration, ...properties }));
  }

  // Manual flush method
  async flushNow(): Promise<void> {
    await this.flush();
  }

  // Cleanup method
  dispose(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush(true);
  }
}

// Export singleton instance
export const logger = new LoggingService();

// Global error boundary helper
export const logReactError = (error: Error, errorInfo: { componentStack?: string | null }) => {
  logger.logException('React Error Boundary', error, {
    componentStack: errorInfo.componentStack || 'No component stack available'
  });
};
